import type File from 'interfaces/File';
import type FileAction from 'interfaces/FileAction';
import type FileActionContext from 'interfaces/FileActionContext';
import { Icon } from '../components/Core/Icon';
import { AiClassificationStatusEnum } from '../enums/AiClassificationStatusEnum';
import { FileLimitationEnum } from '../enums/FileLimitationEnum';
import { FileMenuActionEnum } from '../enums/FileMenuActionEnum';
import { FilesTableEnum } from '../enums/FilesTableEnum';
import { SpecialFolderTypeEnum } from '../enums/SpecialFolderTypeEnum';
import { isAntivirusBlocked } from '../utils/antivirus';
import { hasFileLimitation, isFileFavoritesActionsAvailable } from '../utils/utils';
import { envFlag } from './envFlag';

export type FileActionKey = FileMenuActionEnum | `tag-${number}`;

/** True when the file is a document already classified by the AI (classifying it again re-classifies it). */
export const isAiClassifiedFile = (file: File | undefined) =>
  !!file && !file.isFolder && file.aiOperationStatus === AiClassificationStatusEnum.Done;

/** True when the AI classification action is available for the selected files. */
const canAiClassify = (context: FileActionContext) =>
  context.tags.length > 0 &&
  envFlag(import.meta.env.VITE_APP_AI_ENABLED) &&
  !context.files.some((f) =>
    hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileAIClassification),
  );

/** True when antivirus licence is on and any selected file is blocked by threat name. */
export const isAntivirusBlockedInSelection = (context: FileActionContext): boolean =>
  Boolean(context.authenticatedUser?.isAntivirusEnabled) &&
  context.files.some((f) => !f.isFolder && isAntivirusBlocked(f));

const notAntivirusBlocked = (context: FileActionContext) => !isAntivirusBlockedInSelection(context);

/**
 * True when the file is a folder with password set, no bypass, and no active unlock session.
 * Accepts either a full context or a getter function (e.g. from store) for use in components.
 */
export function isLockedPasswordFolder(
  file: File,
  contextOrGetter: FileActionContext | ((fileId: number) => string | null | undefined),
): boolean {
  const getSession =
    typeof contextOrGetter === 'function'
      ? contextOrGetter
      : (id: number) => contextOrGetter.getFilePasswordSession?.(id) ?? null;
  return file.isFolder && file.isPasswordSet && !file.isPasswordBypass && !getSession(file.id);
}

/** True when a single unlocked password folder can be re-locked (session clear). */
function canRelockUnlockedPasswordFolder(context: FileActionContext): boolean {
  const file = context.files[0];
  return (
    context.files.length === 1 &&
    file != null &&
    file.isFolder &&
    file.isPasswordSet &&
    !file.isPasswordBypass &&
    (context.getFilePasswordSession?.(file.id) ?? null) != null
  );
}

export const fileActions: FileAction[] = [
  {
    key: 'openSubmenu' as FileMenuActionEnum,
    label: 'common.open',
    type: 'submenu',
    icon: <Icon name="ExternalLinkIcon" className="h-5 w-5" />,
    condition: () => true,
    children: [
      {
        key: FileMenuActionEnum.Open,
        label: 'common.open',
        icon: <Icon name="SquareArrowOutUpRight" className="h-5 w-5" />,
        shortcutId: 'file.open',
        condition: (context) => {
          return (
            notAntivirusBlocked(context) &&
            context.files.length === 1 &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileOpen),
            )
          );
        },
      },
      {
        key: FileMenuActionEnum.OpenFileLocally,
        label: 'common.openFileLocally',
        icon: <Icon name="FileKeyIcon" className="h-5 w-5" />,
        condition: (context) => {
          return (
            notAntivirusBlocked(context) &&
            context.files.length === 1 &&
            !context.files[0]?.isFolder &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyLocalFileOpening),
            ) &&
            context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyLocalDefaultOpening),
            )
          );
        },
      },
      {
        key: FileMenuActionEnum.OpenFileOnline,
        label: 'common.openFileOnline',
        icon: <Icon name="PanelTopIcon" className="h-5 w-5" />,
        condition: (context) => {
          return (
            notAntivirusBlocked(context) &&
            context.files.length === 1 &&
            !context.files[0]?.isFolder &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyLocalFileOpening),
            ) &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyLocalDefaultOpening),
            )
          );
        },
      },
      {
        key: FileMenuActionEnum.OpenShortcutOriginalFileLocation,
        label: 'common.openFileLocation',
        icon: <Icon name="FolderSymlinkIcon" className="h-5 w-5" />,
        condition: (context) => {
          return (
            context.files.length === 1 &&
            (context.files?.[0]?.isShortcut ?? false) &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileOpenFileLocation),
            )
          );
        },
      },
    ],
  },

  {
    key: FileMenuActionEnum.Divider,
    type: 'divider',
  },
  {
    key: 'downloadSubmenu' as FileMenuActionEnum,
    label: 'common.download',
    icon: <Icon name="DownloadIcon" className="h-5 w-5" />,
    type: 'submenu',
    condition: () => true,
    children: [
      {
        key: FileMenuActionEnum.Download,
        label: 'common.download',
        icon: <Icon name="CloudDownloadIcon" className="h-5 w-5" />,
        shortcutId: 'file.download',
        condition: (context) => {
          return (
            notAntivirusBlocked(context) &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileDownload),
            )
          );
        },
      },
      {
        key: FileMenuActionEnum.DownloadEncrypted,
        label: 'common.downloadEncrypted',
        icon: <Icon name="ShieldCheckIcon" className="h-5 w-5" />,
        condition: (context) => {
          return (
            notAntivirusBlocked(context) &&
            context.files.length === 1 &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileDownloadEncrypted),
            )
          );
        },
      },
      {
        key: FileMenuActionEnum.KeepOffline,
        label: 'common.keepOffline',
        icon: <Icon name="HardDriveDownloadIcon" className="h-5 w-5" />,
        condition: (context) => {
          return (
            notAntivirusBlocked(context) &&
            // No quick share files and no trash files
            !context.files.some((f) => f.specialFolder === SpecialFolderTypeEnum.QuickShare) &&
            !context.files.some((f) => f.isDeleted) &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyLocalFileOpening),
            )
          );
        },
      },
    ],
  },

  {
    key: FileMenuActionEnum.Divider,
    type: 'divider',
  },

  {
    key: 'shareSubmenu' as FileMenuActionEnum,
    label: 'common.share',
    icon: <Icon name="ShareIcon" className="h-5 w-5" />,
    type: 'submenu',
    condition: () => true,
    children: [
      {
        key: FileMenuActionEnum.Share,
        label: 'common.share',
        icon: <Icon name="Share2Icon" className="h-5 w-5" />,
        shortcutId: 'file.share',
        condition: (context) => {
          return (
            notAntivirusBlocked(context) &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileShare),
            )
          );
        },
      },
      {
        key: FileMenuActionEnum.CopyLink,
        label: 'common.copyLink',
        icon: <Icon name="CopyIcon" className="h-5 w-5" />,
        shortcutId: 'file.copyLink',
        condition: (context) => {
          return context.files.length === 1;
        },
      },
      {
        type: 'divider',
        key: FileMenuActionEnum.Divider,
      },
      {
        key: FileMenuActionEnum.PublicLink,
        label: 'common.publicLink',
        icon: <Icon name="LinkIcon" className="h-5 w-5" />,
        shortcutId: 'file.publicLink',
        condition: (context) => {
          return (
            notAntivirusBlocked(context) &&
            context.files.length === 1 &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFilePublicLink),
            )
          );
        },
      },
    ],
  },

  {
    key: FileMenuActionEnum.SellFile,
    label: 'common.sell',
    icon: <Icon name="StoreIcon" className="h-5 w-5" />,
    condition: (context) => {
      return (
        context.files.length === 1 &&
        !context.files.some((f) =>
          hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileSale),
        ) &&
        envFlag(import.meta.env.VITE_APP_STORE_ENABLED) &&
        (context.authenticatedUser?.isSellEnabled || false)
      );
    },
  },
  {
    key: FileMenuActionEnum.Divider,
    type: 'divider',
  },
  // Password not set: single "Set password" action
  {
    key: FileMenuActionEnum.SetFilePassword,
    label: 'common.setPassword',
    icon: <Icon name="FolderKeyIcon" className="h-5 w-5" />,
    shortcutId: 'file.setPassword',
    condition: (context) => {
      return (
        context.files.length === 1 &&
        context.files[0] != null &&
        !context.files[0].isPasswordSet &&
        !context.files.some((f) =>
          hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenySetFolderPassword),
        )
      );
    },
  },
  // Password set: "Password" submenu with Edit, Remove, Block (Lock)
  {
    key: FileMenuActionEnum.Password,
    label: 'common.password',
    icon: <Icon name="FolderKeyIcon" className="h-5 w-5" />,
    type: 'submenu',
    condition: (context) => {
      return (
        context.files.length === 1 &&
        context.files[0] != null &&
        context.files[0].isPasswordSet &&
        !context.files.some((f) =>
          hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenySetFolderPassword),
        )
      );
    },
    children: [
      {
        key: FileMenuActionEnum.EditPassword,
        label: 'common.edit',
        icon: <Icon name="RectangleEllipsisIcon" className="h-5 w-5" />,
        condition: () => true,
      },
      {
        key: FileMenuActionEnum.RemovePassword,
        label: 'common.remove',
        danger: true,
        icon: <Icon name="KeyboardOffIcon" className="h-5 w-5" />,
        condition: () => true,
      },
      {
        key: FileMenuActionEnum.Divider,
        type: 'divider',
      },
      {
        key: FileMenuActionEnum.Lock,
        label: 'common.lock',
        icon: <Icon name="LockIcon" className="h-5 w-5" />,
        condition: (context) => canRelockUnlockedPasswordFolder(context),
      },
    ],
  },
  // Receiver / DenySetFolderPassword: Lock stays available outside Password submenu.
  // Owner path keeps Lock under Password (gated by DenySetFolderPassword above).
  {
    key: FileMenuActionEnum.Lock,
    label: 'common.lock',
    icon: <Icon name="LockIcon" className="h-5 w-5" />,
    condition: (context) => {
      const file = context.files[0];
      return (
        canRelockUnlockedPasswordFolder(context) &&
        file != null &&
        hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenySetFolderPassword)
      );
    },
  },
  {
    key: FileMenuActionEnum.Rename,
    label: 'common.rename',
    icon: <Icon name="FilePenLineIcon" className="h-5 w-5" />,
    shortcutId: 'file.rename',
    condition: (context) => {
      return (
        context.files.length === 1 &&
        !context.files.some((f) =>
          hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileRename),
        )
      );
    },
  },
  {
    key: FileMenuActionEnum.Organize,
    label: 'common.organize',
    type: 'submenu',
    icon: <Icon name="FolderIcon" className="mt-0.5 h-5 w-5" />,
    condition: () => true,
    children: [
      {
        key: FileMenuActionEnum.Replace,
        label: 'common.replace',
        icon: <Icon name="ReplaceIcon" className="h-5 w-5" />,
        condition: (context) => {
          return (
            context.files.length === 1 &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileReplace),
            )
          );
        },
      },
      {
        key: FileMenuActionEnum.Duplicate,
        icon: <Icon name="FilesIcon" className="h-5 w-5" />,
        label: 'common.createCopy',
        condition: (context) => {
          return !context.files.some((f) =>
            hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileCreateCopy),
          );
        },
      },
      {
        key: FileMenuActionEnum.Move,
        icon: <Icon name="FolderInputIcon" className="h-5 w-5" />,
        label: 'common.move',
        shortcutId: 'file.move',
        condition: (context) => {
          return !context.files.some((f) =>
            hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileMove),
          );
        },
      },
      {
        key: FileMenuActionEnum.ConvertToPdf,
        icon: <Icon name="FileDownIcon" className="h-5 w-5" />,
        label: 'common.convertToPdf',
        condition: (context) => {
          return (
            context.files.length === 1 &&
            !context.files[0]?.isFolder &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileConvertToPdf),
            )
          );
        },
      },
      {
        key: FileMenuActionEnum.CreateShortcut,
        icon: <Icon name="FileSymlinkIcon" className="h-5 w-5" />,
        label: 'common.createShortcut',
        shortcutId: 'file.createShortcut',
        condition: (context) => {
          return (
            context.files.length === 1 &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileCreateShortCut),
            )
          );
        },
      },
      {
        key: FileMenuActionEnum.AddToFavorites,
        label: 'common.addToFavorites',
        icon: <Icon name="StarIcon" className="h-5 w-5" />,
        shortcutId: 'file.favorite',
        condition: (context) => {
          return (
            context.files.length > 0 &&
            context.files.every((file) =>
              isFileFavoritesActionsAvailable(file, context.isInFileRoute),
            ) &&
            !context.files.some((f) => f.isFavourite)
          );
        },
      },
      {
        key: FileMenuActionEnum.RemoveFromFavorites,
        label: 'common.removeFromFavorites',
        icon: <Icon name="StarOffIcon" className="h-5 w-5" />,
        condition: (context) => {
          return (
            context.files.length > 0 &&
            context.files.every((file) =>
              isFileFavoritesActionsAvailable(file, context.isInFileRoute),
            ) &&
            context.files.some((f) => f.isFavourite)
          );
        },
      },
      {
        key: FileMenuActionEnum.Hide,
        label: 'common.hide',
        icon: <Icon name="ArchiveRestoreIcon" className="h-5 w-5" />,
        condition: (context) => {
          return (
            //!context.files.some((f) => f.isArchived) &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileHide),
            )
          );
        },
      },
      {
        key: FileMenuActionEnum.Unhide,
        label: 'common.unhide',
        icon: <Icon name="ArchiveXIcon" className="h-5 w-5" />,
        condition: (context) => {
          return (
            //context.files.some((f) => f.isArchived) &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileUnHide),
            )
          );
        },
      },
      {
        key: FileMenuActionEnum.MarkAsRead,
        label: 'common.markAsRead',
        icon: <Icon name="BookOpenIcon" className="h-5 w-5" />,
        condition: (context) =>
          context.isInFileRoute(FilesTableEnum.Received) &&
          context.files.length >= 1 &&
          context.files.some((f) => f.isUnRead),
      },
      {
        key: FileMenuActionEnum.MarkAsUnread,
        label: 'common.markAsUnread',
        icon: <Icon name="EyeOffIcon" className="h-5 w-5" />,
        condition: (context) =>
          context.isInFileRoute(FilesTableEnum.Received) &&
          context.files.length >= 1 &&
          context.files.every((f) => !f.isUnRead),
      },
    ],
  },
  {
    key: FileMenuActionEnum.Details,
    label: 'common.details',
    icon: <Icon name="InfoIcon" className="h-5 w-5" />,
    shortcutId: 'file.details',
    condition: (context) => {
      return context.files.length === 1 && !context.isRightPanelOpen;
    },
  },

  {
    key: FileMenuActionEnum.Divider,
    type: 'divider',
  },
  {
    key: FileMenuActionEnum.Tags,
    label: 'common.tags',
    icon: <Icon name="TagsIcon" className="mt-0.5 h-5 w-5" />,
    shortcutId: 'file.tags',
    condition: (context) => {
      return (
        context.files.length === 1 &&
        context.tags.length > 0 &&
        !context.files.some((f) =>
          hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileTags),
        )
      );
    },
  },
  {
    key: FileMenuActionEnum.ArtificialIntelligence,
    label: 'common.AIGrant',
    type: 'submenu',
    icon: <Icon name="BrainCircuitIcon" className="mt-0.5 h-5 w-5" />,
    condition: () => envFlag(import.meta.env.VITE_APP_AI_ENABLED),
    children: [
      {
        key: FileMenuActionEnum.Classify,
        label: 'common.classify',
        icon: <Icon name="TagAiIcon" className="mt-0.5 h-5 w-5" />,
        onlyContextMenu: true,
        condition: (context) => notAntivirusBlocked(context) && canAiClassify(context),
      },
      {
        key: FileMenuActionEnum.ArtificialIntelligenceSummary,
        label: 'common.artificialIntelligenceSummary',
        icon: <Icon name="ScanTextIcon" className="mt-0.5 h-5 w-5" />,
        onlyContextMenu: true,
        shortcutId: 'file.aiSummary',
        condition: (context) => {
          return (
            notAntivirusBlocked(context) &&
            context.files.length === 1 &&
            context.tags.length > 0 &&
            !context.files[0]?.isFolder &&
            envFlag(import.meta.env.VITE_APP_AI_ENABLED) &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileAISummary),
            )
          );
        },
      },
      {
        key: FileMenuActionEnum.ArtificialIntelligenceDocumentChat,
        label: 'common.artificialIntelligenceDocumentChat',
        icon: <Icon name="BotMessageSquareIcon" className="mt-0.5 h-5 w-5" />,
        onlyContextMenu: true,
        condition: (context) => {
          return (
            notAntivirusBlocked(context) &&
            context.files.length === 1 &&
            context.tags.length > 0 &&
            !context.files[0]?.isFolder &&
            envFlag(import.meta.env.VITE_APP_AI_ENABLED) &&
            !context.files.some((f) =>
              hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileAIChat),
            )
          );
        },
      },
    ],
  },
  {
    key: FileMenuActionEnum.Divider,
    type: 'divider',
  },
  {
    key: FileMenuActionEnum.Classify,
    label: 'common.classify',
    icon: <Icon name="TagIcon" className="mt-0.5 h-5 w-5" />,
    onlyContextMenu: true,
    condition: (context) =>
      notAntivirusBlocked(context) &&
      context.files.length === 1 &&
      !context.files[0]?.isFolder &&
      canAiClassify(context),
  },
  {
    key: FileMenuActionEnum.Divider,
    type: 'divider',
  },

  {
    key: FileMenuActionEnum.Delete,
    label: 'common.delete',
    icon: <Icon name="TrashIcon" className="h-5 w-5" />,
    danger: true,
    shortcutId: 'file.delete',
    condition: (context) => {
      return !context.files.some((f) =>
        hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileDelete),
      );
    },
  },
  {
    key: FileMenuActionEnum.RestoreFromTrash,
    label: 'common.restoreFromTrash',
    icon: <Icon name="FolderOutputIcon" className="h-5 w-5" />,
    condition: (context) => {
      return (
        context.files.some((f) => f.isDeleted) &&
        !context.files.some((f) =>
          hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileRestoreFromTrash),
        )
      );
    },
  },
  {
    key: FileMenuActionEnum.DeletePermanently,
    label: 'common.deletePermanently',
    icon: <Icon name="TrashIcon" className="h-5 w-5" />,
    danger: true,
    condition: (context) => {
      return (
        context.files.length <= 100 &&
        !context.files.some((f) =>
          hasFileLimitation(f.fileLimitations, FileLimitationEnum.DenyFileDeletePermanently),
        )
      );
    },
  },
];

/**
 * Filters and processes a list of user actions recursively based on conditions and context.
 *
 * @param {FileAction[]} actions - The array of user actions to be filtered and processed recursively.
 * @param {FileActionContext} context - The context to evaluate the conditions of the actions.
 * @return {FileAction[]} A new array of user actions after filtering and recursively processing children actions.
 */
function filterActionsRecursive(
  actions: FileAction[],
  context: FileActionContext,
  withDividers: boolean = true,
  isTopbar: boolean = false,
): FileAction[] {
  const out: FileAction[] = [];

  for (const a of actions) {
    // 1) Basic filters
    if (a.condition && !a.condition(context)) continue;
    if (a.type === 'divider' && !withDividers) continue;
    // keep the original logic: onlyContextMenu items are hidden on topbar
    if (a.onlyContextMenu === isTopbar) continue;

    // 2) Recurse into children if present
    let node: FileAction = { ...a };
    if (a.children) {
      const children = filterActionsRecursive(a.children, context, withDividers, isTopbar);

      if (a.type === 'submenu') {
        if (children.length === 0) {
          // no visible children -> drop this submenu
          continue;
        }
        if (children.length === 1) {
          // exactly one child -> return that child directly (flatten)
          node = children[0]!;
        } else {
          node = { ...a, children };
        }
      } else {
        node = { ...a, children };
      }
    }

    out.push(node);
  }

  // 3) Clean dividers (no duplicates, not first/last)
  const cleaned: FileAction[] = [];
  for (const action of out) {
    if (action.type === 'divider') {
      const prev = cleaned[cleaned.length - 1];
      if (!prev || prev.type === 'divider') continue;
    }
    cleaned.push(action);
  }
  if (cleaned.length && cleaned[cleaned.length - 1]?.type === 'divider') {
    cleaned.pop();
  }

  return cleaned;
}

/**
 * Retrieves a list of filtered user actions based on the provided context.
 * When all selected files are locked password folders, returns only the Unlock action.
 *
 * @param {FileActionContext} context - The context used to filter user actions.
 * @param {boolean} withDividers - Whether to include dividers in the filtered actions.
 * @param {boolean} isTopbar - Whether the actions are being filtered for a topbar context.
 * @return {FileAction[]} A list of user actions that match the filtering criteria.
 */
export function filteredFileActions(
  context: FileActionContext,
  withDividers: boolean = true,
  isTopbar: boolean = false,
): FileAction[] {
  // If at least one selected file is a locked password folder, show only Unlock (most restrictive rule).
  if (context.files.length >= 1 && context.files.some((f) => isLockedPasswordFolder(f, context))) {
    return [
      {
        key: FileMenuActionEnum.Unlock,
        label: 'common.unlock',
        icon: <Icon name="LockOpenIcon" className="h-5 w-5" />,
      },
    ];
  }
  return filterActionsRecursive(fileActions, context, withDividers, isTopbar);
}

/** Returns true when `actionKey` appears in a filtered action tree (including submenus). */
export function isFileActionAvailable(actions: FileAction[], actionKey: FileActionKey): boolean {
  const walk = (nodes: FileAction[]): boolean =>
    nodes.some((node) => {
      if (node.key === actionKey) return true;
      if (node.children?.length) return walk(node.children);
      return false;
    });
  return walk(actions);
}
